import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import Seo from '../../components/Seo';
import { toast } from 'sonner';
import { Loader2, Upload, User, Users, Building2, Trash2, Edit3 } from 'lucide-react';

const PLACEHOLDER = '/inasw-logo.jpg';
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
const SPECIALTIES = ['Anxiety','Depression','Trauma / PTSD','Grief & Loss','Couples & Marriage','Family Therapy','Children & Adolescents','Substance Use','Eating Disorders','LGBTQ+','Veterans','Life Transitions','Stress Management','ADHD'];

const empty = {
  practice_name: '', contact_name: '', email: '', phone: '', website: '',
  practice_type: 'individual', specialties: [], telehealth: false, in_person: true,
  city: '', state: '', zip_code: '', description: '', logo_data: '',
};

const ListYourPractice = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [mine, setMine] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadMine = async () => {
    try {
      const res = await axios.get(`${API_URL}/private-practices/mine`);
      setMine(res.data.practices || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => {
    loadMine();
    setForm((f) => ({ ...f, contact_name: user?.name || '', email: user?.email || '' }));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSpec = (s) => setForm((f) => ({ ...f, specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s] }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_500_000) { toast.error('Logo must be under 1.5MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => set('logo_data', reader.result);
    reader.readAsDataURL(file);
  };

  const startEdit = (p) => {
    setEditingId(p.practice_id);
    setForm({ ...empty, ...p });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const resetForm = () => { setEditingId(null); setForm({ ...empty, contact_name: user?.name || '', email: user?.email || '' }); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.practice_name.trim()) { toast.error('Practice name is required.'); return; }
    if (!form.email.trim()) { toast.error('A contact email is required.'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/private-practices/${editingId}`, form);
        toast.success('Listing updated.');
      } else {
        await axios.post(`${API_URL}/private-practices`, form);
        toast.success('Your practice is now listed!');
      }
      resetForm();
      loadMine();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save listing.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this listing?')) return;
    try { await axios.delete(`${API_URL}/private-practices/${id}`); toast.success('Listing removed.'); if (editingId === id) resetForm(); loadMine(); }
    catch (e) { toast.error('Could not remove listing.'); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] pt-24 pb-16">
      <Seo exactTitle="List Your Private Practice | INASW" description="Add your social work private practice to the INASW directory — free during launch." path="/private-practice/list" noindex />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="w-7 h-7 text-primary" />
          <div>
            <h1 className="font-serif text-3xl font-bold text-primary">{editingId ? 'Edit Your Listing' : 'List Your Private Practice'}</h1>
            <p className="text-gray-500 text-sm">Free during our launch. Attaches to your INASW account — no separate login.</p>
          </div>
        </div>

        {/* Existing listings */}
        {!loading && mine.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6" data-testid="pp-my-listings">
            <h2 className="font-semibold text-primary mb-3">Your Listings</h2>
            <div className="space-y-2">
              {mine.map((p) => (
                <div key={p.practice_id} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`pp-mine-${p.practice_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <img src={p.logo_data || PLACEHOLDER} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-8 h-8 rounded object-cover bg-gray-50" />
                    <span className="font-medium truncate">{p.practice_name}</span>
                    <span className="text-xs text-gray-400">({p.practice_type})</span>
                    {p.status !== 'active' && <span className="text-xs text-red-500">unpublished</span>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(p)} data-testid={`pp-edit-${p.practice_id}`}><Edit3 className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p.practice_id)} className="text-red-600" data-testid={`pp-delete-${p.practice_id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5" data-testid="pp-form">
          {/* Type */}
          <div>
            <Label>Practice Type</Label>
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => set('practice_type', 'individual')} className={`flex-1 border rounded-xl p-3 flex items-center gap-2 justify-center ${form.practice_type === 'individual' ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-gray-200 text-gray-600'}`} data-testid="pp-form-type-individual"><User className="w-4 h-4" /> Individual</button>
              <button type="button" onClick={() => set('practice_type', 'group')} className={`flex-1 border rounded-xl p-3 flex items-center gap-2 justify-center ${form.practice_type === 'group' ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-gray-200 text-gray-600'}`} data-testid="pp-form-type-group"><Users className="w-4 h-4" /> Group Practice</button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Practice Name *</Label><Input value={form.practice_name} onChange={(e) => set('practice_name', e.target.value)} data-testid="pp-form-name" /></div>
            <div><Label>Contact Name</Label><Input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} data-testid="pp-form-contact" /></div>
            <div><Label>Contact Email *</Label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="pp-form-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="pp-form-phone" /></div>
            <div><Label>Website</Label><Input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://" data-testid="pp-form-website" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="pp-form-city" /></div>
            <div>
              <Label>State</Label>
              <select value={form.state} onChange={(e) => set('state', e.target.value)} className="w-full border rounded-md px-3 h-10 text-sm bg-white" data-testid="pp-form-state">
                <option value="">Select…</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><Label>ZIP</Label><Input value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} data-testid="pp-form-zip" /></div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.telehealth} onChange={(e) => set('telehealth', e.target.checked)} data-testid="pp-form-telehealth" /> Offers Telehealth</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_person} onChange={(e) => set('in_person', e.target.checked)} data-testid="pp-form-inperson" /> In-Person</label>
          </div>

          <div>
            <Label>Specialties</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SPECIALTIES.map((s) => (
                <button type="button" key={s} onClick={() => toggleSpec(s)} className={`text-xs rounded-full px-3 py-1.5 border ${form.specialties.includes(s) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200'}`}>{s}</button>
              ))}
            </div>
          </div>

          <div><Label>About the Practice</Label><Textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Describe your approach, who you serve, availability…" data-testid="pp-form-description" /></div>

          <div>
            <Label>Logo / Photo (optional)</Label>
            <div className="flex items-center gap-3 mt-2">
              <img src={form.logo_data || PLACEHOLDER} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-16 h-16 rounded-xl object-cover border bg-gray-50" />
              <label className="cursor-pointer inline-flex items-center gap-2 border border-primary text-primary rounded-lg px-4 py-2 text-sm hover:bg-primary hover:text-white">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="pp-form-logo" />
              </label>
              {form.logo_data && <button type="button" onClick={() => set('logo_data', '')} className="text-sm text-red-600">Remove</button>}
            </div>
            <p className="text-xs text-gray-400 mt-1">No logo? We'll show the INASW badge.</p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90 text-white" data-testid="pp-form-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : (editingId ? 'Save Changes' : 'Publish Listing (Free)')}
            </Button>
            {editingId && <Button type="button" variant="outline" onClick={resetForm}>Cancel edit</Button>}
            <Button type="button" variant="ghost" onClick={() => navigate('/private-practice')}>View Directory</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ListYourPractice;
